import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const r = await login(email.trim(), password);
    setSubmitting(false);
    if (!r.ok) {
      setError(r.error || "Erro ao iniciar sessão");
      toast.error(r.error || "Erro ao iniciar sessão");
    } else {
      toast.success("Sessão iniciada");
    }
  }

  return (
    <div className="min-h-screen w-full flex bg-white">
      <Toaster position="top-right" richColors />
      {/* Left form */}
      <div className="w-full lg:w-1/2 flex flex-col p-10 lg:p-16">
        <div className="flex items-center gap-3 mb-16">
          <div className="w-9 h-9 bg-[#0047FF] flex items-center justify-center rounded-sm">
            <Building2 className="w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-chivo font-black text-neutral-950 text-base leading-none tracking-tight">
              CONDO<span className="text-[#0047FF]">CRM</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 mt-1.5 font-chivo font-bold">
              Gestão · PT
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-4">
              Área reservada
            </div>
            <h1 className="text-4xl sm:text-5xl font-chivo font-black text-neutral-950 tracking-tight mb-3 leading-[1.05]">
              Bem-vindo <span className="text-[#0047FF]">de volta.</span>
            </h1>
            <p className="text-base font-manrope text-neutral-600 mb-10 leading-relaxed">
              Aceda à plataforma para gerir os condomínios que a sua empresa presta serviços.
            </p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Email
                </label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@empresa.pt"
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-3 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] transition-all outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Palavra-passe
                </label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-3 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] transition-all outline-none"
                />
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm font-manrope text-[#FF4500] border border-[#FF4500]/20 bg-[#FF4500]/5 px-4 py-3 rounded-sm"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-3.5 font-manrope font-semibold text-sm transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? "A entrar…" : "Entrar"}
                {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
              </button>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-xs font-manrope text-neutral-500 hover:text-[#0047FF] transition-colors"
                >
                  Esqueci a palavra-passe?
                </Link>
              </div>
            </form>

            <div className="mt-12 text-xs font-manrope text-neutral-400">
              © {new Date().getFullYear()} CondoCRM — Todos os direitos reservados.
            </div>
          </div>
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:block lg:w-1/2 relative bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwYWJzdHJhY3QlMjBzaGFwZXN8ZW58MHx8fHwxNzg1MjQwMTg2fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-950/60 via-neutral-950/30 to-neutral-950/70" />
        <div className="relative h-full flex flex-col justify-between p-16 text-white">
          <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-300 font-chivo font-bold">
            Plataforma B2B · v1.0
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-300 font-chivo font-bold mb-6">
              — Feito para empresas de gestão condominial
            </div>
            <h2 className="text-4xl xl:text-5xl font-chivo font-black tracking-tight leading-[1.05] mb-6">
              Estrutura, clareza e controlo <span className="text-[#0047FF]">total</span> sobre cada
              edifício.
            </h2>
            <p className="text-base font-manrope text-neutral-300 max-w-md leading-relaxed">
              Centralize condomínios, serviços prestados e ocorrências numa única
              interface arquitetada para operações rápidas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
